import React, { useEffect, useRef, useState } from "react";
import { canUseDOM } from "history/DOMUtils";
import type {
    T1InjectionOptionsType,
    T1InjectionType,
} from "../../../../../common-deprecated/hooks/useT1ModalInjection";
import { getFinanceExternalConfigurationObject } from "../../utils/typedUtils";
import type { ExternalConfigType } from "../../../../shared-logic/types/ExternalPriceType";
import { FINANCE_CALCULATOR, INSURANCE_CALCULATOR } from "../../../../../common-deprecated/constants";
import { EnvironmentEnum, getThemeClassname } from "../../../../../common-deprecated/utils";
import Debug from "../../../../../common-deprecated/Debug";
import type { MakolabInsuranceCompleteEventType } from "../../../../shared-logic/types/FinanceInsuranceRateTypes";
import {
    setFinanceCollapsable,
    setInsuranceCollapsable,
    setInsurancePersonalized,
    updateQuoteConfig,
} from "../../redux/actions/FinanceInsuranceActions";
import { getInjectedModuleURL } from "../../utils/settings/bnbFinanceSettingUtils";
import { useBnbSelector } from "../../redux/store";
import { isPSLSDataValidOnFinance } from "../../utils/FinanceInsuranceCalculatorLink";
import { useDispatch } from "../../../../../shared-logic/redux/store";
import { hasMakolabInsurance as hasMakolabInsuranceFnc } from "../../../../shared-logic/utils/insuranceUtils";
import {
    prepareCalculator,
    resetCalculator,
    setFinanceOption,
} from "../../../../../common-deprecated/redux/actions/UtilActions";

const IDENTIFIER_PREFIX = "cc-injection";

export type FinanceInsuranceCalculatorType = typeof FINANCE_CALCULATOR | typeof INSURANCE_CALCULATOR;

export type ExternalCalculatorPropType = {
    type: FinanceInsuranceCalculatorType;
    showLoader: boolean;
    loader: JSX.Element;
    visible: boolean;
    className?: string;
};

const FinanceInsuranceCalculator = (props: ExternalCalculatorPropType): JSX.Element | null => {
    const { type, className, visible, showLoader, loader } = props;
    const basket = useBnbSelector((state) => state.basket);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const calculatorReady = useBnbSelector((state) => state.utils.calculatorReady);
    const car = useBnbSelector((state) => state.car);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const externalPrice = useBnbSelector((state) => state.externalPrice);

    const hasMakolabInsurance = type === INSURANCE_CALCULATOR && hasMakolabInsuranceFnc(bnbSettings);

    const items = useBnbSelector((state) => state.items);
    const loadPSLS =
        (type === FINANCE_CALCULATOR && bnbSettings.addPSLSToFinanceConfigurationObject) ||
        (type === INSURANCE_CALCULATOR && bnbSettings.addPSLSToInsuranceConfigurationObject);

    const psls = useBnbSelector((state) => state.psls);
    const finance = useBnbSelector((state) => state.finance);
    const { quotes } = finance;
    const save = useBnbSelector((state) => state.save);
    const shouldSaveFirst =
        type === FINANCE_CALCULATOR && bnbSettings.sentFinanceStepToyotaCode && !bnbSettings.enableFinanceCompare;

    const tradeInAemExternalData = useBnbSelector((state) => state.tradeInAemExternalData);

    // Finance Compare doesn't need a valid ccGetMonthlyRate response before showing the calculator so PSLS check is not required for finance compare
    const allowedToRender =
        visible && (bnbSettings.enableFinanceCompare || isPSLSDataValidOnFinance(type, finance, bnbSettings));

    const [components, setComponents] = useState<T1InjectionType | null>(null);
    const [mounted, setMounted] = useState<boolean>(false);
    const dispatch = useDispatch();

    const container = useRef(null);
    const containerID = `${IDENTIFIER_PREFIX}-${type}-amd`;
    // MOUNT/UNMOUNT HOOKS
    useEffect(() => {
        if (canUseDOM && window && window.require) {
            const moduleUrl = hasMakolabInsurance
                ? bnbSettings.insuranceModuleURL
                : `${commonSettings.urlWebsiteInjection}/scripts/T1-injection-components.js`;
            // Require the amd module responsible for loading the code injected frames
            window.require([moduleUrl as string], (comps: T1InjectionType) => {
                setComponents(comps);
            });
        }
        return () => {
            document.dispatchEvent(new CustomEvent("bss-destroy-connector-event"));
            dispatch(resetCalculator(type));
        };
    }, []);

    useEffect(() => {
        if (!visible) return;
        dispatch(prepareCalculator(shouldSaveFirst, loadPSLS));
    }, [visible]);

    useEffect(() => {
        // If it should not be visible, return
        // eslint-disable-next-line no-useless-return
        if (!calculatorReady) return;
        if (components && allowedToRender) {
            // Personalized finance quotes also get added in the method getExternalConfigurationObject
            type AdditionalConfigType = { ConfigurationCode?: string; Environment?: string };
            const conf: ExternalConfigType & AdditionalConfigType = getFinanceExternalConfigurationObject(
                commonSettings,
                car,
                basket,
                items,
                tradeInAemExternalData,
                finance.quotes,
                externalPrice,
                finance.financeProductType,
            );

            // Append the code if it exists
            if (shouldSaveFirst && !save.error && save.data && save.data.ConfigurationCode !== "") {
                conf.ConfigurationCode = save.data.ConfigurationCode;
            }

            // Append the psls data if it exists
            if (loadPSLS && psls.data) {
                conf.PSLS = psls.data;
            }

            // Add the parameter "environment:financeCalculatorEnvironment" to the configuration object sent for the finance frame
            if (type === FINANCE_CALCULATOR) {
                if (bnbSettings.financeCalculatorEnvironment) {
                    conf.Environment = bnbSettings.financeCalculatorEnvironment;
                } else if (commonSettings.environment === EnvironmentEnum.Preview) {
                    conf.Environment = "test";
                }
            }

            // Quick fix; cfr LB-839 once makolab's calculator is updated to support both TotalPriceInclPromotions and TotalPrice
            // currently this is not the case hence makolab shows the monthly rate based on TotalPrice while it should be based on TotalPriceInclPromotions
            // This is only relevant for the finance calculator + temporary, hence implementation is done here rather than in the getExternalConfigurationObject method
            if (conf.TotalPriceInclPromotions) {
                conf.TotalPrice = conf.TotalPriceInclPromotions;
                delete conf.TotalPriceInclPromotions;
            }

            // Check the querystring if the new format should be used or not
            // TODO Should be removed in the future and made finance dependent!
            const toBeSentConfiguration = commonSettings.query.useExtendedFinanceConfig
                ? {
                      Configuration: conf,
                      // TODO After MakoLab implementation started/completed more work will be required here
                      AdditionalData: { FixedHeaderSelector: "" },
                  }
                : conf;
            // TODO Keep the following line until implementation is completed from makolab's side, this way it's easy to see what configuration object is posted
            Debug.info("Sending configuration object: ", toBeSentConfiguration);
            if (!mounted) {
                // Mount & inject the content
                try {
                    // Append the content
                    if (container && container.current) {
                        // We add an extra container because the injected calculator also adjusts classNames on it which would otherwise cause React errors.
                        const extraDivContainer = document.createElement("div");
                        extraDivContainer.id = containerID;
                        (container.current as HTMLDivElement).appendChild(extraDivContainer);
                    }

                    const themeClassName = getThemeClassname(car.model.id);
                    const options: T1InjectionOptionsType = {
                        target: `#${containerID}`,
                        configuration: toBeSentConfiguration,
                        location: "body",
                        ...(commonSettings.brand === "toyota" && themeClassName && { theme: themeClassName }),
                    };
                    if (hasMakolabInsurance) {
                        options.isAppendedToBody = false;

                        // Add event listeners to the insurance container, this matches *roughly* with the insurance calculator logic in the usc financeInsuranceUtils.ts
                        // search for the "timContainer" in the code base to find occurrences
                        const timContainer = document.getElementById(containerID);
                        if (timContainer) {
                            timContainer.addEventListener("TIM_WIDGET_CLOSED", () => {
                                setMounted(false);
                                dispatch(setInsuranceCollapsable(false));
                            });
                            timContainer.addEventListener(
                                "TIM_WIDGET_OFFER_CONFIRM",
                                (event: any) => {
                                    // this.hideModal.bind(this);
                                    const detail = event?.detail as MakolabInsuranceCompleteEventType;
                                    if (detail?.quoteId) {
                                        dispatch(setInsurancePersonalized(true));
                                        dispatch(updateQuoteConfig({ insurance: detail.quoteId }, false));
                                        dispatch(setInsuranceCollapsable(false));
                                    }
                                },
                                {
                                    once: true,
                                },
                            );
                        }
                    } else {
                        const HIDE_MODAL_EXT_EVENT = "hide-build-amd-modal";
                        document.addEventListener(HIDE_MODAL_EXT_EVENT, () => {
                            // UPDATE OF FINANCE DATA IS NOT TRIGGERED
                            dispatch(setFinanceCollapsable(false));
                        });
                        options.url = getInjectedModuleURL(commonSettings, bnbSettings, type);
                    }

                    components.inject(options);
                    setMounted(true);
                } catch (ex) {
                    Debug.error("error injecting component: ", containerID);
                }
            } else {
                // Send out update call with configuration to makolab
                document.dispatchEvent(
                    new CustomEvent("calculator-configuration-update", {
                        detail: {
                            configuration: toBeSentConfiguration,
                        },
                    }),
                );
            }
        } else if (container && container.current && allowedToRender) {
            // Clear the container
            (container.current as HTMLDivElement).innerHTML = "";
        }

        if (finance.quotes.finance) {
            dispatch(setFinanceOption("monthly"));
        }
    }, [
        basket,
        bnbSettings,
        calculatorReady,
        car,
        container,
        containerID,
        commonSettings,
        components,
        dispatch,
        externalPrice.co2Specs.value,
        hasMakolabInsurance,
        loadPSLS,
        items,
        tradeInAemExternalData,
        psls.data,
        quotes,
        shouldSaveFirst,
        save.error,
        save.data,
        type,
        visible,
    ]);
    if (!allowedToRender && !mounted) return null;
    return (
        <>
            {showLoader && loader}
            <div
                ref={container}
                style={{ minHeight: "350px", display: showLoader ? "none" : "block" }}
                className={className}
            />
        </>
    );
};

export default FinanceInsuranceCalculator;
